import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  customers: [],
  selected_customer: null,
  loading: false,
  isSuccess: false,
  errors: [],
  recordsTotal: 0,
  deleteFeedback: [],
  deleteFeedbackLoading: false,
  deleteFeedbackError: null,
  deletedAccounts: [],
  deletedAccountsLoading: false
};

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/user/customers/all', { params });
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// New Async Thunks for blocking and unblocking customers
export const blockCustomer = createAsyncThunk(
  'customers/blockCustomer',
  async ({ id, reason }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/block/${id}`, { reason });
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const unblockCustomer = createAsyncThunk(
  'customers/unblockCustomer',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/unblock/${id}`);
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/user/client/${id}`);
      return id;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchDeleteAccountFeedback = createAsyncThunk(
  'customers/fetchDeleteAccountFeedback',
  async (email, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/delete-account-feedback/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Snapshot of every deleted CLIENT account: the only place where the identity
// (firstname / lastname / email) of a soft-deleted client is still readable.
export const fetchDeletedAccounts = createAsyncThunk(
  'customers/fetchDeletedAccounts',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/delete-account-feedback', {
        params: { accountType: 'CLIENT' }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomers.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.customers = payload.data || [];
        state.recordsTotal = payload.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchCustomers.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(blockCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(blockCustomer.fulfilled, (state, { payload }) => {
        state.isSuccess = true;

        state.customers = state.customers.map((customer) =>
          customer._id === payload._id ? { ...customer, locked: true } : customer
        );
        state.loading = false;
      })
      .addCase(blockCustomer.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(unblockCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(unblockCustomer.fulfilled, (state, { payload }) => {
        state.isSuccess = true;

        state.customers = state.customers.map((customer) =>
          customer._id === payload._id ? { ...customer, locked: false } : customer
        );
        state.loading = false;
      })
      .addCase(unblockCustomer.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCustomer.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.customers = state.customers.filter((customer) => customer.DT_RowId !== payload);
        state.loading = false;
      })
      .addCase(deleteCustomer.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(fetchDeleteAccountFeedback.pending, (state) => {
        state.deleteFeedbackLoading = true;
        state.deleteFeedback = [];
        state.deleteFeedbackError = null;
      })
      .addCase(fetchDeleteAccountFeedback.fulfilled, (state, { payload }) => {
        state.deleteFeedbackLoading = false;
        state.deleteFeedback = Array.isArray(payload) ? payload : [];
        state.deleteFeedbackError = null;
      })
      .addCase(fetchDeleteAccountFeedback.rejected, (state, { payload, error }) => {
        state.deleteFeedbackLoading = false;
        state.deleteFeedback = [];
        state.deleteFeedbackError = payload?.message || error?.message || 'Error';
      })
      .addCase(fetchDeletedAccounts.pending, (state) => {
        state.deletedAccountsLoading = true;
      })
      .addCase(fetchDeletedAccounts.fulfilled, (state, { payload }) => {
        state.deletedAccountsLoading = false;
        state.deletedAccounts = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchDeletedAccounts.rejected, (state) => {
        state.deletedAccountsLoading = false;
        state.deletedAccounts = [];
      });
  }
});

export default customerSlice.reducer;
